/**
 * Who is doing this case — FR-E5, T-2.5.
 *
 * Two things on this screen are the requirement rather than layout.
 *
 * **The open-case count is beside every name.** FR-E5 asks for it by name, and
 * it is the only information that makes this dialog a decision rather than a
 * dropdown: a manager assigning work needs to know who is already carrying
 * eleven.
 *
 * **The list is not the firm's staff, and the screen says so.** ProDesk cannot
 * list a firm's members — ADR-0012 §2 puts membership in core and
 * `/internal/evaluate` answers for one user at a time — so the server derives
 * the pool from the cases the firm has already assigned and sets
 * `pool_is_derived`. Presenting that as *"your staff"* would be a lie the firm
 * can check, so it is presented as what it is, with the sentence that tells
 * somebody how to add a name to it.
 *
 * **And from w5a, the list is not the only way in.** The derived pool is empty
 * on a firm that has never assigned anything, so the only option was *Nobody*
 * and *Next in rotation* answered *"give one case to somebody by hand and the
 * rotation has a list to work from"* — with no by hand anywhere in the product.
 * A circular dead end on a firm's first day (the Wave 4 close, §5.2). The way
 * out is the field below the picker: a user id, typed or pasted from the
 * FiveOX console, which `POST /cases/{id}/assign` has always accepted.
 *
 * **Why a user id and not a list of the firm's members.** Spec §9-20 — the
 * platform read that would list them — is unanswered, and there is no
 * org-management client in `app/platform` to call. Inventing one against a
 * contract nobody has read is the plausible-but-wrong the constitution forbids,
 * and a shadow member table is what CLAUDE.md §7 forbids. So the honest thing
 * is a field that says exactly what it wants and where to get it. The moment
 * §9-20 lands this input becomes a `<select>` and nothing else here changes.
 *
 * The id is checked for **shape** before it is sent, and the screen says what
 * it cannot check: ProDesk does not know who works at this firm, so it cannot
 * tell a colleague's id from a typo. A wrong id assigns the case to nobody who
 * will ever see it, and the firm finds out from the workload screen.
 *
 * Nothing here decides whether this person may assign: `can_assign` comes from
 * the server on the same payload, because the server is the thing that refuses.
 *
 * **C27 (spec §10.19 item 6; UAT report #2).** The list is the whole firm's —
 * it was narrowed to the case's branch, which FR-E5 asks only of the rotation —
 * and **an empty list is said in a sentence**, not drawn as a dropdown whose
 * one option is *Nobody*, which read as a dropdown that had failed to load.
 */

import { Alert, Button, Dialog, Input, Select } from '@fiveox/ui'
import { useEffect, useState } from 'react'

import { ApiError } from '../../lib/api'
import { type Candidate, type CaseDetail, caseFlows } from '../../lib/api/cases'
import { CONSOLE_URL } from '../../lib/config'
import { DialogActions, FORM_GRID, FormRow } from '../../components/page'

export function AssignDialog({
  detail,
  onClose,
  onAssigned,
}: {
  detail: CaseDetail
  onClose: () => void
  onAssigned: () => void
}) {
  const [pool, setPool] = useState<Candidate[]>([])
  const [canAssign, setCanAssign] = useState(true)
  const [derived, setDerived] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [chosen, setChosen] = useState(detail.case.assignee_user_id ?? '')
  const [typed, setTyped] = useState('')
  const [reviewer, setReviewer] = useState(detail.case.reviewer_user_id ?? '')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    void caseFlows
      .candidates({ case_id: detail.case.id })
      .then((body) => {
        setPool(body.data)
        setCanAssign(Boolean(body.meta?.can_assign))
        setDerived(Boolean(body.meta?.pool_is_derived))
        setLoaded(true)
      })
      .catch(() => setError('The list of people could not be loaded.'))
  }, [detail.case.id])

  // Shape only. ProDesk cannot check that a user id belongs to somebody at this
  // firm — that is spec §9-20 — so this refuses the typo it *can* see and the
  // sentence below owns the one it cannot.
  const trimmed = typed.trim()
  const looksLikeId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    trimmed,
  )
  const typedIsBad = trimmed.length > 0 && !looksLikeId
  // The typed id wins when it is there: somebody who has gone to the trouble of
  // pasting one means it, and silently preferring a stale dropdown value would
  // assign the case to the wrong person with no way to tell.
  const assignee = looksLikeId ? trimmed : chosen

  const send = async (body: Parameters<typeof caseFlows.assign>[1]) => {
    setSaving(true)
    setError(null)
    try {
      await caseFlows.assign(detail.case.id, body)
      onAssigned()
    } catch (err) {
      // The round-robin's "there is nobody to rotate to" arrives here as a
      // CONFLICT whose message says what the firm has to do. Shown verbatim:
      // rewriting it in the screen is how the two explanations drift.
      setError(err instanceof ApiError ? err.message : 'The case could not be assigned.')
    } finally {
      setSaving(false)
    }
  }

  return (
    // The kit's dialog (2026-09-25): a bounded panel whose body scrolls, so
    // the title and the close button stay reachable however tall this gets.
    // Laid out as the Branches dialog is since Wave 15C (C14 §10.6 item 7).
    <Dialog open onClose={onClose} title="Assign this case" error={error} width="lg">
      <p className="text-muted-foreground mb-4 text-sm">
        {detail.case.number} · {detail.service.name}
      </p>

      {!canAssign && (
        <div className="mb-4">
          <Alert tone="warning">
            Only a partner or a practice manager can hand a case to somebody else.
          </Alert>
        </div>
      )}

      <div className={FORM_GRID}>
        {loaded && pool.length === 0 ? (
          <FormRow label="Assign to">
            <p className="text-muted-foreground text-sm sm:pt-3" data-testid="assign-empty-pool">
              Nobody has been assigned a case in this firm yet, so there is no one to pick. Enter
              the person’s user id, or assign the first case from the FiveOX console.
            </p>
          </FormRow>
        ) : (
          <FormRow label="Assign to" htmlFor="assign-to">
            <Select
              id="assign-to"
              value={chosen}
              onChange={(e) => setChosen(e.target.value)}
              disabled={!canAssign}
              // The honest sentence. A firm that reads "your staff" here and does
              // not find somebody who has a login will file a bug against the
              // wrong system.
              hint={
                derived
                  ? 'This list is the people your firm has already given a case to. Members are added to ProDesk in the FiveOX console; somebody who has never been assigned work appears here after their first case.'
                  : undefined
              }
            >
              <option value="">Nobody</option>
              {pool.map((person) => (
                <option key={person.user_id} value={person.user_id}>
                  {person.user_id.slice(0, 8)} — {person.open_cases} open
                  {person.open_on_service > 0 ? ` (${person.open_on_service} on this service)` : ''}
                </option>
              ))}
            </Select>
          </FormRow>
        )}

        <FormRow
          label={pool.length === 0 ? 'Assign to a user id' : 'Or somebody not on that list'}
          htmlFor="assign-typed"
        >
          <Input
            id="assign-typed"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            disabled={!canAssign}
            spellCheck={false}
            placeholder="00000000-0000-0000-0000-000000000000"
            className="font-mono text-xs"
            error={
              typedIsBad ? 'That is not a user id. Copy it from the member’s row in the console.' : null
            }
          />
          {!typedIsBad && (
            <p className="text-muted-foreground mt-1.5 text-xs">
              Their user id, from your firm’s members in the{' '}
              <a
                href={`${CONSOLE_URL}/organizations`}
                target="_blank"
                rel="noreferrer"
                className="text-primary underline"
              >
                FiveOX console
              </a>
              . ProDesk does not hold your firm’s member list, so it cannot tell a colleague’s id
              from a typo — check it before you assign. After the first case they appear in the
              list above and the rotation can reach them.
            </p>
          )}
        </FormRow>

        <FormRow label="Reviewer" htmlFor="assign-reviewer" optional>
          <Select
            id="assign-reviewer"
            value={reviewer}
            onChange={(e) => setReviewer(e.target.value)}
            disabled={!canAssign}
            // ADR-0012 §5. The service's own default is the *role* on its
            // review stage, set in the workflow editor — not a person.
            hint="Left blank, the review goes to the role the service’s workflow names."
          >
            <option value="">Whoever the workflow names</option>
            {pool.map((person) => (
              <option key={person.user_id} value={person.user_id}>
                {person.user_id.slice(0, 8)}
              </option>
            ))}
          </Select>
        </FormRow>
      </div>

      <DialogActions>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="outline"
          disabled={!canAssign || saving}
          onClick={() => void send({ round_robin: true })}
        >
          Next in rotation
        </Button>
        <Button
          disabled={!canAssign || saving || typedIsBad}
          loading={saving}
          loadingText="Saving…"
          onClick={() =>
            void send({
              // The empty string is *"nobody"*, which is a different request
              // from "leave it alone" — the API takes a flag for it rather
              // than a null, for the reason `CasePatch` documents.
              ...(assignee ? { assignee_user_id: assignee } : { clear_assignee: true }),
              ...(reviewer ? { reviewer_user_id: reviewer } : { clear_reviewer: true }),
            })
          }
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  )
}
